import { Box, Space, SpaceVertical, Spinner, ToggleSwitch, Tooltip } from '@looker/components';
import { BIIcon, BIIncludeExclude, BISearchBar, globalTheme } from '@pulsepointinc/bi-components';
import type { StateKey } from '@pulsepointinc/bi-components/dist/components/BIIncludeExclude/IncludeExclude.type';
import { DsTab, DsTabBar, DsTypography } from '@pulsepointinc/design-system-react';
import type React from 'react';
import { type FC, useCallback } from 'react';
import { SmartSearchChecker } from '../../../../../../components/SmartSearchChecker';
import { MIN_SEARCH_PHRASE_LENGTH } from '../../../../../../helpers';
import type { ICustomFilter } from '../../../../../../helpers/helpers';
import { TOUR_STEP_IDS } from '../../../../../../providers/TourProvider';
import type { Filter } from '../../../../../../stores/filters';
import { BulkUploadComponent, BulkUploadFeedback } from '../../BulkUpload';
import { FilterSearchNoResults } from '../../FilterSearchNoResults/Index';
import { BulkUpdateSection } from './BulkUpdateSection';
import { useIncludeExcludeFilter } from './hooks/useIncludeExcludeFilter';
import { IncludeExcludeListLoader } from './IncludeExcludeListLoader';
import { SelectedOnlySection } from './SelectedOnlySection';
import { StyledBulkButton } from './styles';
import type { IncludeExcludeTab } from './types';

interface FilterProps extends ICustomFilter {
  filterName: string;
  label?: string;
  onMultiFilterChange?: (filter: Filter) => void;
  selectedOnlyLabel?: string;
  renderCategory?: (category: string, state?: StateKey | 'semi-check' | 'semi-block') => React.ReactNode;
}

export const IncludeExcludeFilter: FC<FilterProps> = ({
  filterName,
  label = '',
  onChange,
  onExpressionTypeChange,
  onMultiFilterChange,
  selectedOnlyLabel,
  renderCategory,
}) => {
  const {
    activeTab,
    setActiveTab,
    searchTerm,
    filterState,
    suggestions,
    isLoading,
    mode,
    selectedCategories,
    advancedSearchEnabled,
    toggleAdvanceSearch,
    handleSearchTermChange,
    handleFilterChange,

    isBulkUploadMode,
    bulkUploadResult,
    setIsBulkUploadMode,
    clearBulkUploadResult: clearBulkResult,
    handleBulkUploadComplete,
    enableBulkUpload,
    vectorField,
  } = useIncludeExcludeFilter({
    filterName,
    onChange,
    onExpressionTypeChange,
    onMultiFilterChange,
  });

  const renderTabContent = useCallback(() => {
    if (activeTab === 'all') {
      return (
        <SpaceVertical
          gap="u3"
          height="100%"
          align="stretch"
          padding="12px 24px 0"
          data-tour-id={TOUR_STEP_IDS.INCLUDE_EXCLUDE_FILTER_CONTAINER}
        >
          {bulkUploadResult && (
            <BulkUploadFeedback
              title={`Bulk Upload finished: ${bulkUploadResult.unrecognizedItems.length} lines were not parsed.`}
              items={bulkUploadResult.unrecognizedItems}
              onDismiss={clearBulkResult}
            />
          )}

          <BISearchBar
            data-tour-id={TOUR_STEP_IDS.INCLUDE_EXCLUDE_FILTER_SEARCH}
            defaultValue={searchTerm}
            onSearchChange={handleSearchTermChange}
            placeholder="Search"
            showClearButton
            after={
              vectorField ? (
                <SmartSearchChecker>
                  <Space gap="u2">
                    <Tooltip placement="top" content={'AI-powered Smart search'}>
                      <Space gap="u1" align="center" color={globalTheme.colors.textSecondary}>
                        {isLoading && !!searchTerm && <Spinner />}
                        <BIIcon icon="SmartSearch" />
                        <ToggleSwitch on={advancedSearchEnabled} onChange={toggleAdvanceSearch} />
                      </Space>
                    </Tooltip>
                  </Space>
                </SmartSearchChecker>
              ) : (
                isLoading && !!searchTerm && <Spinner />
              )
            }
          />

          {searchTerm && searchTerm.length >= MIN_SEARCH_PHRASE_LENGTH && !!suggestions.length && (
            <BulkUpdateSection
              filterState={filterState}
              handleFilterChange={handleFilterChange}
              categories={suggestions}
              mode={mode}
            />
          )}

          {advancedSearchEnabled && !searchTerm && !suggestions.length && (
            <DsTypography>Please enter a search term to use advance search</DsTypography>
          )}

          {isLoading && !searchTerm && !suggestions.length ? (
            <IncludeExcludeListLoader />
          ) : !isLoading && searchTerm && !suggestions.length ? (
            <FilterSearchNoResults />
          ) : (
            <BIIncludeExclude
              mode={mode}
              initialState={filterState}
              categories={suggestions}
              onToggleChange={handleFilterChange}
              renderCategory={renderCategory}
            />
          )}
        </SpaceVertical>
      );
    }

    if (activeTab === 'selected') {
      return (
        <SpaceVertical gap="none" height="100%">
          {bulkUploadResult && (
            <Box width="100%" padding="12px 24px 0">
              <BulkUploadFeedback
                title={`Bulk Upload finished: ${bulkUploadResult.unrecognizedItems.length} lines were not parsed.`}
                items={bulkUploadResult.unrecognizedItems}
                onDismiss={clearBulkResult}
              />
            </Box>
          )}

          <SelectedOnlySection
            mode={mode}
            selected={selectedCategories}
            state={filterState}
            onChange={handleFilterChange}
            render={renderCategory}
          />
        </SpaceVertical>
      );
    }
  }, [
    searchTerm,
    handleSearchTermChange,
    vectorField,
    isLoading,
    advancedSearchEnabled,
    toggleAdvanceSearch,
    filterState,
    handleFilterChange,
    suggestions,
    mode,
    selectedCategories,
    renderCategory,
    bulkUploadResult,
    clearBulkResult,
    activeTab,
  ]);

  if (isBulkUploadMode) {
    return (
      <Box px="u6" pb="u6" width="100%" height="100%">
        <BulkUploadComponent
          title={label}
          filterName={filterName}
          initialExpressionType={mode === 'block' ? 'block' : 'target'}
          disableExpression={mode !== undefined}
          onCancel={() => setIsBulkUploadMode(false)}
          onUpload={handleBulkUploadComplete}
        />
      </Box>
    );
  }

  return (
    <SpaceVertical gap="none" position={'relative'} width="100%" height="100%">
      {enableBulkUpload && (
        <StyledBulkButton
          color="invisible"
          iconBefore={<BIIcon icon="Download" />}
          onClick={() => setIsBulkUploadMode(true)}
        >
          Bulk Upload {label}
        </StyledBulkButton>
      )}
      <DsTabBar
        layout="panel"
        value={activeTab}
        onValueChange={(e) => setActiveTab(e.target.value as IncludeExcludeTab)}
      >
        <DsTab key="all" value="all" label={`All ${label}`} />
        <DsTab
          key="selected"
          value="selected"
          label={`${selectedOnlyLabel ?? 'Selected Only'} (${selectedCategories.length})`}
        />
      </DsTabBar>
      <Box width="100%" flex={1} minHeight={0}>
        {renderTabContent()}
      </Box>
    </SpaceVertical>
  );
};
